/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import { Component, inject } from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { EChartsOption, EChartsType } from "echarts";
import { BehaviorSubject, combineLatest, defer, map, Observable } from "rxjs";
import { ButtonComponent } from "../../../../shared/components/button/button.component";
import { ChartComponent } from "../../../../shared/components/chart/chart.component";
import { ChipsComponent } from "../../../../shared/components/chips/chips.component";
import { DropdownComponent } from "../../../../shared/components/dropdown/dropdown.component";
import { BarChart } from "../../../../shared/models/http/charts/bar-chart/bar-chart.model";
import { ChartResponse } from "../../../../shared/models/ui/charts.model";
import { ChipOption } from "../../../../shared/models/ui/chip-option.model";
import { CloudScopeMode } from "../../../../shared/models/ui/cloud-scope-mode.model";
import { CloudScopeOrderBy } from "../../../../shared/models/ui/cloud-scope-orderby.model";
import { SelectOption } from "../../../../shared/models/ui/select-option.model";
import { AuthorizationService } from "../../../../shared/services/authorization.service";
import { DashboardCloudService } from "../../../../shared/services/dashboard-cloud.service";
import { DashboardStateService } from "../../../../shared/services/state/dashboard-state.service";
import { StorageService } from "../../../../shared/services/storage.service";
import { CLOUD_ORDER_BY_LOCATION_OPTIONS, COLORS } from "../../../../shared/utils/constants";
import { EMISSIONS_ANALYSIS_BAR_CHART_OVERRIDES } from "../../../../shared/utils/echarts/chart-constants";
import { GRANTS } from "../../../../shared/utils/grants.constants";
import { lazyLoad, Loadable } from "../../../../shared/utils/lazy-load";
import { formatDateForBE, saveAs } from "../../../../shared/utils/utils";

type CloudLocationsFilters = {
	tenantId: string;
	startDate: string;
	endDate: string;
	cloudProviders: string[];
	locations: string[];
	applications: string[];
	scopeMode: CloudScopeMode;
	orderBy: CloudScopeOrderBy;
};

@Component({
	selector: "globalictcc-applications",
	standalone: true,
	imports: [
		AsyncPipe,
		ChipsComponent,
		DropdownComponent,
		ChartComponent,
		ReactiveFormsModule,
		ButtonComponent
	],
	templateUrl: "./cloud-locations.component.html",
	styleUrl: "./cloud-locations.component.scss"
})
export class CloudLocationsComponent {
	private readonly _document: Document = inject(DOCUMENT);
	private readonly _dashboardCloudService = inject(DashboardCloudService);
	private readonly _dashboardStateService = inject(DashboardStateService);
	private readonly _storageService = inject(StorageService);
	public readonly authorizationService = inject(AuthorizationService);

	public readonly grants = GRANTS.cloud;
	public orderByOptions: SelectOption[] = CLOUD_ORDER_BY_LOCATION_OPTIONS;
	public orderByControl = new FormControl(CLOUD_ORDER_BY_LOCATION_OPTIONS[0].value);

	public EmissionsAnalysisBarChartColors: string[] = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.WHITE
	];
	public emissionsAnalysisBarChartOverrides = EMISSIONS_ANALYSIS_BAR_CHART_OVERRIDES;
	public emissionsAnalysisByScopeBarChartOverrides: EChartsOption = {
		series: { stack: "bar" }
	};
	public totalEmissionPieChartColors: string[] = [];

	// Only the views granted to the user are selectable, so only their API is ever called.
	public chips: ChipOption[] = [
		{
			label: "PERCENTAGE VIEW",
			value: "PERCENTAGE",
			grant: this.grants.locationsPercentageView
		},
		{ label: "TRUE VALUE VIEW", value: "VALUE", grant: this.grants.locationsTrueValueView }
	]
		.filter(chip => this.authorizationService.canSee(chip.grant))
		.map(({ label, value }, index) => ({ label, value, selected: index === 0 }));

	private readonly _scopeMode$ = new BehaviorSubject<CloudScopeMode>(
		(this.chips[0]?.value as CloudScopeMode) ?? "PERCENTAGE"
	);
	private readonly _orderBy$ = new BehaviorSubject<CloudScopeOrderBy>(
		this.orderByControl.value as CloudScopeOrderBy
	);

	private readonly _filters$: Observable<CloudLocationsFilters | undefined> = combineLatest([
		defer(() => this._dashboardStateService.cloudAssetsGlobalForm$),
		this._scopeMode$,
		this._orderBy$
	]).pipe(
		map(([form, scopeMode, orderBy]) => {
			const startDate = formatDateForBE(form?.timePeriod?.start);
			const endDate = formatDateForBE(form?.timePeriod?.end);

			if (!form?.applications || !form.cloudProviders || !form.locations) return undefined;
			if (!startDate || !endDate) return undefined;

			return {
				tenantId: this._storageService.getTenantId(),
				startDate,
				endDate,
				cloudProviders: form.cloudProviders,
				locations: form.locations,
				applications: form.applications,
				scopeMode,
				orderBy
			};
		})
	);

	public readonly emissionsAnalysisByScopeBarChart$: Observable<
		Loadable<ChartResponse<BarChart>>
	> = lazyLoad(this._filters$, filters =>
		this._dashboardCloudService.getRegionsEmissions(
			filters.tenantId,
			filters.startDate,
			filters.endDate,
			filters.cloudProviders,
			filters.locations,
			filters.applications,
			filters.scopeMode,
			filters.orderBy
		)
	);

	/**
	 *  Handles  the change  of  scope  mode (percentage  or  absolute  value).
	 *  Triggers a  refresh  of  the emissions  analysis  chart.
	 *  @param  mode The  selected  scope  mode.
	 */
	public onModeChange(mode: string | number) {
		this._scopeMode$.next(mode as CloudScopeMode);
	}

	/**
	 *  Handles  the  change of  sorting  order  for the  chart  data.
	 *  Updates  the form  control  and  triggers a  chart  refresh.
	 *  @param  orderBy The  selected  order  by value.
	 */
	public onOrderByChange(orderBy: string | string[]) {
		this.orderByControl.setValue(orderBy as CloudScopeOrderBy, { emitEvent: false });
		this._orderBy$.next(orderBy as CloudScopeOrderBy);
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}
}
